// import { DATE_FORMATS } from "@parent-app/config";
// import { ChildrenStoreData, LoginResponse, LoginStorageData } from "@parent-app/types";
import { endOfWeek, format, fromUnixTime } from 'date-fns'
import { MessageImageName, REPLY_MESSAGE_SEPARATOR_DASHES } from 'src/app/config/appConstants';
import { DATE_FORMATS, NOTIFICATION_EVENT_TYPE } from 'src/shared/config/src';
import { AttachmentType, ChildrenStoreData, CommonMessageListItemType, CommunicationResponseItem, ConversationResponseItem, EymemoryLoginStorageData, InboxResponseItem, LoginResponse, LoginStorageData, MessageDetailAttachmentType, NurseryGroupStoreData, SentBoxResponseItem, ValidateOrderResponse } from 'src/shared/types/src';
import { Buffer } from "buffer";
import tinycolor from "tinycolor2";

export const getLoginStoreData = (loginResponse: LoginResponse): LoginStorageData | undefined => {
  if (!loginResponse) return undefined;
  let storeData: LoginStorageData = {} as LoginStorageData;
  storeData.username = loginResponse.username;
  storeData.termsAccepted = loginResponse.termsAccepted;
  storeData.showAppTour = loginResponse.showAppTour;
  storeData.accessToken = loginResponse.accessToken;
  storeData.refreshToken = loginResponse.refreshToken;

  const childrenArr: ChildrenStoreData[] = [];

  loginResponse.instances.forEach((instance) => {
    instance.nurseries.forEach((nursery) => {
      nursery.children.forEach((child) => {
        const childObj: ChildrenStoreData = {
          ...child,
          instanceUrl: instance.instanceUrl,
          instanceToken: nursery.X_INSTANCE_TOKEN,
          eyMemoriesUrl: nursery.EY_MEMORIES_URL,
          nursery: {
            id: nursery.nursery_id,
            name: nursery.name,
            logo: nursery.logo,
            nurseryInstanceKey: nursery.nurseryInstanceKey,
            eymanNurseryId: nursery.eyman_nursery_id,
            brandingColor: nursery.brandingColor
          },
          systemsEnabled: { ...nursery.systemsEnabled },
          systemAccess: { ...child.systemAccess }
        }
        childrenArr.push(childObj);
      })
    })
  })

  storeData.children = childrenArr;
  return storeData;
}

export const groupChildrenByNursery = (allChildren: ChildrenStoreData[]) => {
  const nurseryGroupArr: NurseryGroupStoreData[] = [];
  allChildren.map((child: ChildrenStoreData) => {
    const existingNursery = nurseryGroupArr.find((n: NurseryGroupStoreData) => n.nurseryInstanceKey === child?.nursery?.nurseryInstanceKey);
    if (!existingNursery) {
      nurseryGroupArr.push({
        id: child?.nursery?.id,
        name: child?.nursery?.name,
        logo: child?.nursery?.logo,
        nurseryInstanceKey: child?.nursery?.nurseryInstanceKey,
        children: [child],
        brandingColor: child?.nursery?.brandingColor
      })
    } else {
      existingNursery.children.push(child);
    }
  })

  return nurseryGroupArr;
}

export const getNameInitials = (name: string) => {
  if (!name) return '';
  if (name.indexOf(' ') != -1) {
    const nameArr = name.split(' ');
    return `${nameArr[0].charAt(0)}${nameArr[1].charAt(0)}`.toUpperCase();
  } else {
    return name.charAt(0).toUpperCase();
  }
};

export const dateFormatter = (date: string | Date, formatStr: string = 'dd MMM, yyyy') => {
  try {
    if (!date) return '';
    if (typeof date === 'string') {
      if (date.includes('0000-00-00')) return '';
      date = date.toString().trim();
      date = new Date(date);
    }
    return format(date, formatStr);
  } catch (error) {
    return '';
  }
}

export const formatUnixTimestamp = (timestamp: string, formatStr: string = 'dd MMM, yyyy') => {
  try {
    if (!timestamp || isNaN(Number(timestamp))) return '';
    return format(fromUnixTime(Number(timestamp)), formatStr)
  } catch (error) {
    return '';
  }
}

export const statusFormatter = (status: string) => {
  if (!status) return '';
  return status.toLowerCase().split('_').map((str) => str.charAt(0).toUpperCase() + str.slice(1)).join(' ');
}

export const getFormattedAmount = (amount: string, currency: string, withSign: boolean = true) => {
  let amountToReturn: string;
  if (!amount || isNaN(Number(amount))) return '';
  if (amount && amount.toString().startsWith('-')) {
    amountToReturn = `-${currency ?? ''}${amount.toString().replace('-', '') ?? '-'}`;
  } else {
    amountToReturn = `${currency ?? ''}${amount}`;
    if (Number(amount) !== 0) {
      amountToReturn = `+${amountToReturn}`;
    }
    // amountToReturn = `${(Number(amount) !== 0) ? '+' : ''}${currency}${amount}`;
  }
  if (!withSign && (amountToReturn.startsWith('-')) || amountToReturn.startsWith('+')) {
    return amountToReturn.slice(1);
  }
  return amountToReturn;
}

export const getCurrentWeekEndDate = () => {
  return format(endOfWeek(new Date()), DATE_FORMATS.YMD);
}

export const convertDurationToSeconds = (duration: string) => {
  const [hours, minutes] = duration.split(':');
  if (isNaN(Number(hours)) || isNaN(Number(minutes))) return 0;
  return Number(hours) * 60 * 60 + Number(minutes) * 60;
}

export const convertSecondsToTimeString = (seconds: number): string => {
  if (isNaN(seconds)) return '';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  let timeString = '';

  if (hours > 0) {
    timeString += `${hours} hrs `;
  }

  if (minutes > 0) {
    timeString += `${minutes} mins`;
  }

  return timeString.trim();
}

export const appendQueryParams = (
  url: string,
  paramsToAdd: Record<string, string | number | boolean>
): string => {
  // Separate the base URL and any existing query string or hash
  const [baseWithQuery, hashFragment] = url.split('#');
  const [baseUrl, queryString] = baseWithQuery.split('?');

  // Parse existing query params into an object
  const queryParams: Record<string, string> = {};
  if (queryString) {
    for (const param of queryString.split('&')) {
      const [key, value] = param.split('=');
      if (key) {
        queryParams[decodeURIComponent(key)] = decodeURIComponent(value || '');
      }
    }
  }

  // Add or update with new parameters
  for (const [key, value] of Object.entries(paramsToAdd)) {
    queryParams[key] = String(value);
  }

  // Build new query string
  const newQueryString = Object.entries(queryParams)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

  // Rebuild the final URL
  let finalUrl = `${baseUrl}?${newQueryString}`;
  if (hashFragment) {
    finalUrl += `#${hashFragment}`;
  }

  return finalUrl;
}

// to sort an array of objects by key in ascending order
export const sortArrayByKey = (array: any[], key: string) => {
  return array.sort((a, b) => {
    if (a[key] < b[key]) return -1;
    if (a[key] > b[key]) return 1;
    return 0;
  });
};

// const navigateToTimelineScreen = (navigation: any) => {
//   navigation.navigate(TIMELINE_TAB);
// }

// const navigateToDailyDiaryDetailScreen = (navigation: any, entityDetails?: string) => {
//   if (entityDetails) {
//     const parsedData = JSON.parse(entityDetails);
//     const date = parsedData.date;
//     if (date) {
//       navigation.navigate(APP_SCREEN.DAILY_DIARY_DETAILS, { date });
//     } else {
//       navigateToTimelineScreen(navigation);
//     }
//   } else {
//     navigateToTimelineScreen(navigation);
//   }
// }

// const navigateToPlanningScreen = (navigation: any, entityDetails?: string) => {
//   if (entityDetails) {
//     const parsedData = JSON.parse(entityDetails);
//     const date = parsedData.date;
//     if (date) {
//       navigation.navigate(APP_SCREEN.HOME_LEARNING, { screen: PLANNING, fromDate: date });
//     } else {
//       navigateToTimelineScreen(navigation);
//     }
//   } else {
//     navigateToTimelineScreen(navigation);
//   }
// }

// const navigateToWebViewScreen = (navigation: any, url: string, webViewTitle: string) => {
//   navigation.navigate(APP_SCREEN.WEB_VIEW, {
//     url: url,
//     handleNavigationChange: () => { },
//     handleOnMessageChange: () => { },
//     webViewTitle: webViewTitle
//   });
// }

// const navigateToTimelineScreenWithFilterApplied = (navigation: any, appliedFilterTypes: string[], entityDetails?: string) => {
//   if (entityDetails) {
//     const parsedData = JSON.parse(entityDetails);
//     const date = parsedData.date;
//     if (date) {
//       store.dispatch(
//         TimelineActions.updateFilters({
//           from: date,
//           to: date,
//           isFilterApplied: true,
//           selectedTypes: appliedFilterTypes
//         })
//       );
//     }
//   }
//   navigateToTimelineScreen(navigation);
// }

// export const handleNavigationFromNotifications = (eventType: string, navigation: any, entityId: number, entityDetails?: string) => {
//   switch (eventType) {
//     case NOTIFICATION_EVENT_TYPE.INVOICE_SENT:
//       navigation.navigate(APP_SCREEN.INVOICE);
//       break;
//     case NOTIFICATION_EVENT_TYPE.OBSERVATION_PUSLISHED:
//       navigation.navigate(APP_SCREEN.OBSERVATION_DETAILS, { observationId: entityId });
//       break;
//     case NOTIFICATION_EVENT_TYPE.OBSERVATION_COMMENT_ADDED:
//       navigation.navigate(APP_SCREEN.OBSERVATION_DETAILS, { observationId: entityId });
//       break;
//     case NOTIFICATION_EVENT_TYPE.DD_SHARED:
//       navigateToDailyDiaryDetailScreen(navigation, entityDetails);
//       break;
//     case NOTIFICATION_EVENT_TYPE.DD_PUBLISHED:
//       navigateToDailyDiaryDetailScreen(navigation, entityDetails);
//       break;
//     case NOTIFICATION_EVENT_TYPE.ACCIDENT_SHEET_CREATED:
//       navigation.navigate(APP_SCREEN.ACCIDENT_SHEET_DETAILS, { sheetID: entityId });
//       break;
//     case NOTIFICATION_EVENT_TYPE.MEDICINE_FORM_CREATED:
//       navigation.navigate(APP_SCREEN.MEDICINE_FORM_DETAILS, { medicineFormID: entityId });
//       break;
//     case NOTIFICATION_EVENT_TYPE.OBSERVATION_LOVED:
//       navigation.navigate(APP_SCREEN.OBSERVATION_DETAILS, { observationId: entityId });
//       break;
//     case NOTIFICATION_EVENT_TYPE.ACCIDENT_SHEET_COMPLETED:
//       navigation.navigate(APP_SCREEN.ACCIDENT_SHEET_DETAILS, { sheetID: entityId });
//       break;
//     case NOTIFICATION_EVENT_TYPE.MEDICINE_FORM_REJECTED:
//       if (entityDetails) {
//         const parsedData = JSON.parse(entityDetails);
//         navigation.navigate(APP_SCREEN.CREATE_UPDATE_MEDICINE_FORM, { medicineFormID: entityId, status: parsedData.status });
//       } else {
//         navigation.navigate(APP_SCREEN.CREATE_UPDATE_MEDICINE_FORM, { medicineFormID: entityId });
//       }
//       break;
//     case NOTIFICATION_EVENT_TYPE.MEDICINE_FORM_APPROVED:
//       navigation.navigate(APP_SCREEN.MEDICINE_FORM_DETAILS, { medicineFormID: entityId });
//       break;
//     case NOTIFICATION_EVENT_TYPE.MEDICINE_FORM_SUBMITTED:
//       navigation.navigate(APP_SCREEN.MEDICINE_FORM_DETAILS, { medicineFormID: entityId });
//       break;
//     case NOTIFICATION_EVENT_TYPE.MEDICINE_FORM_DOSE_PUBLISHED:
//       navigation.navigate(APP_SCREEN.MEDICINE_FORM_DETAILS, { medicineFormID: entityId });
//       break;
//     case NOTIFICATION_EVENT_TYPE.SUMMATIVE_REPORT_PUBLISHED:
//       navigateToTimelineScreenWithFilterApplied(navigation, [ACTIVITY_TYPE.REPORTS], entityDetails);
//       break;
//     case NOTIFICATION_EVENT_TYPE.TRANSITION_REPORT_PUBLISHED:
//       navigateToTimelineScreenWithFilterApplied(navigation, [ACTIVITY_TYPE.REPORTS], entityDetails);
//       break;
//     case NOTIFICATION_EVENT_TYPE.CUSTOM_SUMMATIVE_REPORT_PUBLISHED:
//       navigateToTimelineScreenWithFilterApplied(navigation, [ACTIVITY_TYPE.REPORTS], entityDetails);
//       break;
//     case NOTIFICATION_EVENT_TYPE.CUSTOM_TRANSITION_REPORT_PUBLISHED:
//       navigateToTimelineScreenWithFilterApplied(navigation, [ACTIVITY_TYPE.REPORTS], entityDetails);
//       break;
//     case NOTIFICATION_EVENT_TYPE.PLANNING_SHEET:
//       navigateToPlanningScreen(navigation, entityDetails);
//       break;
//     case NOTIFICATION_EVENT_TYPE.PROGRESS_CHECK_REPORT_PUBLISHED:
//       navigateToTimelineScreenWithFilterApplied(navigation, [ACTIVITY_TYPE.REPORTS], entityDetails);
//       break;
//     case NOTIFICATION_EVENT_TYPE.DOCUMENT_ADDED:
//       navigation.navigate(APP_SCREEN.HOME_LEARNING, { screen: RESOURCES_AND_DOCUMENTS });
//       break;
//     case NOTIFICATION_EVENT_TYPE.MESSAGE_RECEIVED:
//       navigation.navigate(CONVERSATION_TAB, { screen: INBOX });
//       break;
//     case NOTIFICATION_EVENT_TYPE.EYENGAGE_POST_PUBLISHED:
//       navigation.navigate(APP_SCREEN.EYENGAGE_POST_DETAILS, { postId: entityId });
//       break;
//     case NOTIFICATION_EVENT_TYPE.EYENGAGE_COMMENT_ADDED:
//       navigation.navigate(APP_SCREEN.EYENGAGE_POST_DETAILS, { postId: entityId });
//       break;
//     case NOTIFICATION_EVENT_TYPE.EYENGAGE_POST_REMINDER:
//       navigation.navigate(APP_SCREEN.EYENGAGE_POST_DETAILS, { postId: entityId });
//       break;
//     case NOTIFICATION_EVENT_TYPE.SIGN_IN_OUT_ADDED:
//       navigateToTimelineScreenWithFilterApplied(navigation, [], entityDetails);
//       break;
//     case NOTIFICATION_EVENT_TYPE.INSTANT_BOOKING_CONFIRMATION:
//       navigation.navigate(CONVERSATION_TAB, { screen: COMMUNICATIONS });
//       break;
//     case NOTIFICATION_EVENT_TYPE.APPROVAL_BOOKING_REQUEST:
//       navigation.navigate(CONVERSATION_TAB, { screen: COMMUNICATIONS });
//       break;
//     case NOTIFICATION_EVENT_TYPE.APPROVAL_BOOKING_CONFIRMATION:
//       navigation.navigate(CONVERSATION_TAB, { screen: COMMUNICATIONS });
//       break;
//     case NOTIFICATION_EVENT_TYPE.APPROVAL_BOOKING_CANCELLATION:
//       navigation.navigate(CONVERSATION_TAB, { screen: COMMUNICATIONS });
//       break;
//     case NOTIFICATION_EVENT_TYPE.INVOICE_STATEMENT_SENT:
//       navigation.navigate(CONVERSATION_TAB, { screen: COMMUNICATIONS });
//       break;
//     case NOTIFICATION_EVENT_TYPE.TRANSACTION_STATEMENT_SENT:
//       navigation.navigate(CONVERSATION_TAB, { screen: COMMUNICATIONS });
//       break;
//     case NOTIFICATION_EVENT_TYPE.MESSAGES_SENT:
//       navigation.navigate(CONVERSATION_TAB, { screen: COMMUNICATIONS });
//       break;
//     case NOTIFICATION_EVENT_TYPE.PROFILE_FORM_SENT:
//       navigation.navigate(CONVERSATION_TAB, { screen: COMMUNICATIONS });
//       break;
//     case NOTIFICATION_EVENT_TYPE.PAYMENT_RECEIPT:
//       navigation.navigate(CONVERSATION_TAB, { screen: COMMUNICATIONS });
//       break;
//     case NOTIFICATION_EVENT_TYPE.DEBTORS_LETTER_SENT:
//       navigation.navigate(CONVERSATION_TAB, { screen: COMMUNICATIONS });
//       break;
//     case NOTIFICATION_EVENT_TYPE.CONVERSATION_MESSAGE_RECEIVED:
//       const state = store.getState() as RootState;
//       const currConversationID = state.conversation?.currConversationID;
//       if (currConversationID) {
//         if (currConversationID !== entityId.toString()) {
//           navigation.goBack();
//           navigation.navigate(APP_SCREEN.CONVERSATION_MESSAGES_SCREEN, {
//             conversationId: entityId
//           });
//         }
//       } else {
//         navigation.navigate(APP_SCREEN.CONVERSATION_MESSAGES_SCREEN, {
//           conversationId: entityId
//         });
//       }
//       break;
//     default:
//       break;
//   }
// }

// const navigateToEymemoriesScreen = (navigation: any, discountCode: string, discountPercent: string) => {
//   if (!discountCode) {
//     return
//   }
//   const rootState = store.getState() as RootState
//   const currentChild = getCurrentChild(rootState);
//   const eyMemoriesUrl = currentChild.eyMemoriesUrl;
//   const encodedDiscountCode = Buffer.from(discountCode).toString("base64");
//   if (eyMemoriesUrl) {
//     const eyMemoriesUrlWithDiscount = appendQueryParams(eyMemoriesUrl, {
//       discountCode: encodedDiscountCode,
//       discountPercent: discountPercent,
//     });
//     if (eyMemoriesUrlWithDiscount) {
//       navigateToWebViewScreen(navigation, eyMemoriesUrlWithDiscount, 'eymemories')
//     }
//   }
// }

// GLOBAL NOTIFICATIONS REDIRECTION METHOD

// export const handleNavigationForGlobalNotification = (navigation: any, payloadData?: NotificationPayloadDataType) => {
//   const eventType = (payloadData?.eventType ?? '').toLowerCase();
//   if (eventType) {
//     switch (eventType) {
//       case NOTIFICATION_EVENT_TYPE.EYMEMORIES_DISCOUNT.toLowerCase():
//         const discountCode = payloadData?.discountCode;
//         const discountPercent = payloadData?.discountPercent;
//         if (discountCode && discountPercent) {
//           navigateToEymemoriesScreen(navigation, discountCode, discountPercent);
//         }
//         break
//       case NOTIFICATION_EVENT_TYPE.GENERAL_WEBLINK.toLowerCase():
//         const url = payloadData?.url;
//         if (!url) {
//           return;
//         }
//         const webTitle = payloadData?.webTitle ?? '';
//         navigateToWebViewScreen(navigation, url, webTitle);
//         break
//     }
//   }
// }

export const transformCommunicationItemToCommonMessageListItem = (item: CommunicationResponseItem): CommonMessageListItemType => {
  return {
    id: item.id.toString(),
    iconType: MessageImageName.mail,
    title: item.subject,
    isRead: item.isRead,
    unReadCount: item.isRead ? 0 : 1,
    createdAt: item.createdAt,
    lastMessage: {
      content: item.message?.trim(),
      timestamp: item.createdAtHuman,
    },
    hasAttachments: item.hasAttachments,
    directParticipant: {
      name: '',
      initials: item?.sentBy?.name ? getNameInitials(item?.sentBy?.name) : ''
    },
    messageType: item?.messageType
  };
};

export const transformInboxItemToCommonMessageListItem = (item: InboxResponseItem): CommonMessageListItemType => {
  return {
    id: item.id.toString(),
    iconType: MessageImageName.mail,
    title: item.subject,
    isRead: item?.self?.isViewed,
    unReadCount: item?.self?.isViewed ? 0 : 1,
    createdAt: item.createdAt,
    lastMessage: {
      content: item.message,
      timestamp: item.createdAt
    },
    hasAttachments: item.hasAttachments
  };
};

export const transformSentBoxItemToCommonMessageListItem = (item: SentBoxResponseItem): CommonMessageListItemType => {
  return {
    id: item.id.toString(),
    iconType: MessageImageName.mail,
    title: item.subject,
    isRead: true,
    unReadCount: 0,
    createdAt: item.createdAt,
    lastMessage: {
      content: item.message.split(REPLY_MESSAGE_SEPARATOR_DASHES)[0],
      timestamp: item.createdAt,
    },
    hasAttachments: item.hasAttachments
  };
};

export const transformAttachmentTypeToMessageAttachmentPropType = (object: AttachmentType): MessageAttachmentPropsType => {
  return {
    name: object.name,
    url: object.downloadUrl
  };
}

export const transformMessageDetailAttachmentTypeToMessageAttachmentPropType = (object: MessageDetailAttachmentType): MessageAttachmentPropsType => {
  return {
    name: object.fileName,
    url: object.url
  };
}

export const groupArrayByObjectKey = (array: any[], key: string) => {
  return array.reduce((result, item) => {
    const groupKey = item[key];
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {});
}

export const sortArrayByTimestamp = (conversations: ConversationResponseItem[]): ConversationResponseItem[] => {
  return conversations.sort((a, b) => {
    const dateA = new Date(a.timestamp);
    const dateB = new Date(b.timestamp);

    return dateB.getTime() - dateA.getTime(); // Descending order
  });
}

export const getTimerSecondsFormattedString = (seconds: number): string => {
  if (seconds >= 3600) {
    return new Date(seconds * 1000).toISOString().substring(11, 16);
  } else {
    return new Date(seconds * 1000).toISOString().substring(14, 19)
  }
}

export const getUrlExtension = (url: string): string | undefined => {
  return url.split(/[#?]/)[0].split(".")?.pop()?.trim();
}

export const getFileNameFromUrl = (url: string): string | null => {
  if (!url) {
    console.error("URL is required");
    return null;
  }

  // Regular expression to extract the filename from the URL path
  const regex = /\/([^/]+)$/;
  const match = url.match(regex);

  if (match) {
    return match[1]; // Filename is the part after the last "/"
  } else {
    console.error("No filename found in the URL.");
    return null;
  }
}



export const getEymemoryLoginStoreData = (loginResponse: ValidateOrderResponse, orderHash: string): EymemoryLoginStorageData | undefined => {
  if (!loginResponse) return undefined;
  let storeData: EymemoryLoginStorageData = {} as EymemoryLoginStorageData;
  storeData = {
    accessToken: loginResponse.accessToken,
    orderHash
  }
  return storeData;
}

export const isColorLight = (color: string): boolean => {
  const c = tinycolor(color);
  return c.isLight();
}

export const getContrastColor = (color: string): string => {
  return isColorLight(color) ? "#121212" : "#FFFFFF";
}

export const getBrandingBorderColor = (brandingColor: string, isDarkMode: boolean) => {
  const isBrandingColorLight = tinycolor(brandingColor).getLuminance() > 0.5;

  // Case 1: Dark branding + Dark theme -> add border (lighter variant for contrast)
  if (!isBrandingColorLight && isDarkMode) {
    return { showBorder: true, borderColor: tinycolor(brandingColor).lighten(40).toHexString() };
  }

  // Case 2: Light branding + Light theme -> add border (darker variant for contrast)
  if (isBrandingColorLight && !isDarkMode) {
    return { showBorder: true, borderColor: tinycolor(brandingColor).darken(25).toHexString() };
  }

  // Case 3 + 4: Other combos → no border
  return { showBorder: false, borderColor: "transparent" };
};

export const getForegroundColor = (color: string): string => {
  return getContrastColor(color);
}